// src/adminFlow/AdminOrderDetailsPage.js
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import API_BASE from '../apiBase';
import { ArrowLeft, Home } from 'lucide-react';
import useAutoRefresh from './useAutoRefresh';
import { useListenInvalidation } from './adminInvalidation';

function fmt(n, c='CAD') {
  try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: c }).format(Number(n||0)); }
  catch { return `$${Number(n||0).toFixed(2)}`; }
}
function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleString(); } catch { return String(d); }
}

export default function AdminOrderDetailsPage() {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [order, setOrder] = useState(null);
  const [items, setItems] = useState([]);

  const reload = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/api/admin/orders/${id}`, { credentials: 'include', cache: 'no-store' });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'Load failed');
      setOrder(j.order); setItems(j.items||[]);
    } catch (e) {
      setErr(e.message || 'Load error');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { reload(); }, [reload]);

  useAutoRefresh(reload, { intervalMs: 30000, refetchOnFocus: true });
  useListenInvalidation(() => reload());

  const totals = useMemo(() => {
    const c = order?.currency || 'CAD';
    return {
      subtotal: fmt(order?.subtotal, c),
      discount: fmt(order?.discount_amount, c),
      tax: fmt(order?.tax_amount, c),
      shipping: fmt(order?.shipping_fee, c),
      total: fmt(order?.total_price, c),
    };
  }, [order]);

  if (loading) return <div className="p-8">Loading…</div>;
  if (err) return <div className="p-8 text-red-700">{err}</div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Order #{order.id}</h1>
          <div className="flex items-center gap-2">
            <Link to="/admin" className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">
              <Home className="w-4 h-4" /> Dashboard
            </Link>
            <Link to="/admin/orders" className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800">
              <ArrowLeft className="w-4 h-4" /> Back to Orders
            </Link>
          </div>
        </div>

        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <div className="text-sm text-gray-500">Customer</div>
            <div className="text-gray-900 font-medium">{order.customer_email}</div>
            <div className="mt-3 text-sm text-gray-500">Placed</div>
            <div className="text-gray-900">{fmtDate(order.order_date)}</div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <div className="text-sm text-gray-500">Status</div>
            <div className="mt-1">
              <span className="px-2 py-1 rounded-full text-xs bg-orange-50 text-orange-700 border border-orange-100">
                {order.status}
              </span>
            </div>
            <div className="mt-3 text-sm text-gray-500">Shipping Status</div>
            <div className="text-gray-900">{order.shipping_status || '—'}</div>
            <div className="mt-3 text-sm text-gray-500">Tracking #</div>
            <div className="text-gray-900">{order.tracking_number || '—'}</div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <div className="text-sm text-gray-500">Ship To</div>
            <div className="text-gray-900 font-medium">{order.shipping?.name || '—'}</div>
            <div className="text-gray-700 text-sm">
              {[order.shipping?.address1, order.shipping?.address2].filter(Boolean).join(', ')}<br/>
              {[order.shipping?.city, order.shipping?.region, order.shipping?.postal_code].filter(Boolean).join(', ')}<br/>
              {order.shipping?.country}
            </div>
          </div>
        </div>

        {/* Items */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b font-semibold text-gray-800">Items</div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-left text-gray-600">
                  <th className="py-3 px-4">Product</th>
                  <th className="py-3 px-4">Variant</th>
                  <th className="py-3 px-4">Unit Price</th>
                  <th className="py-3 px-4">Qty</th>
                  <th className="py-3 px-4">Line Total</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td className="py-6 px-4 text-gray-500" colSpan={5}>No items.</td></tr>
                ) : items.map(it => (
                  <tr key={it.id} className="border-t last:border-b-0">
                    <td className="py-3 px-4 text-gray-900">{it.product_name}</td>
                    <td className="py-3 px-4 text-gray-700">{it.pack_size || '—'}</td>
                    <td className="py-3 px-4">{fmt(it.unit_price, order.currency)}</td>
                    <td className="py-3 px-4">{it.quantity}</td>
                    <td className="py-3 px-4 font-medium">{fmt(it.line_total, order.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="px-5 py-4 border-t">
            <div className="ml-auto w-full max-w-sm space-y-1 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Subtotal</span>
                <span className="text-gray-900">{totals.subtotal}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Discount</span>
                <span className="text-gray-900">-{totals.discount}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Tax</span>
                <span className="text-gray-900">{totals.tax}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Shipping</span>
                <span className="text-gray-900">{totals.shipping}</span>
              </div>
              <div className="h-px bg-gray-100 my-2" />
              <div className="flex items-center justify-between font-semibold">
                <span className="text-gray-800">Total</span>
                <span className="text-gray-900">{totals.total}</span>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
